import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { sendFreeformFeedback } from "@/lib/feedback";

const MAX_LEN = 4000;

/**
 * Header entry point for freeform beta feedback. Route/concept/build context
 * is attached automatically (lib/feedback.ts) — the tester only writes the
 * comment.
 */
export function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState("");

  const send = useMutation({
    mutationFn: (text: string) => sendFreeformFeedback({ comment: text }),
    onSuccess: () => {
      setComment("");
      setOpen(false);
      toast.success("Feedback sent — thank you!");
    },
    onError: () => {
      toast.error("Couldn't send feedback — please try again.");
    },
  });

  const trimmed = comment.trim();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Send feedback"
          title="Send feedback"
          className="flex size-8 items-center justify-center rounded-md border border-border text-muted-foreground hover:border-primary hover:text-primary"
        >
          <MessageSquarePlus className="size-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (trimmed && !send.isPending) send.mutate(trimmed);
          }}
          className="space-y-2"
        >
          <p className="text-sm font-medium">Send beta feedback</p>
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={MAX_LEN}
            rows={4}
            placeholder="What's confusing, broken, or missing?"
            aria-label="Feedback comment"
            autoFocus
          />
          <div className="flex items-center justify-between">
            {/* Says where it goes, not just what's attached: the comment is
                relayed to the team's chat. Identity is not — the relay carries
                an account id, never your email (audit 2026-07-28, MEDIUM-9). */}
            <span className="text-xs text-muted-foreground">
              Your current page is attached. Goes to the PaperChase team.
            </span>
            <Button
              type="submit"
              size="sm"
              disabled={!trimmed || send.isPending}
            >
              {send.isPending ? "Sending…" : "Send"}
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}
